import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import Slide from '@material-ui/core/Slide';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';

import ClearIcon from '@material-ui/icons/Close';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import GetAppIcon from '@material-ui/icons/GetApp';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import NewReleasesIcon from '@material-ui/icons/NewReleases';
import GavelIcon from '@material-ui/icons/Gavel';
import CampaignIcon from '@material-ui/icons/Announcement';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import ImageIcon from '@material-ui/icons/Image';
import TableChartIcon from '@material-ui/icons/TableChart';
import DescriptionIcon from '@material-ui/icons/Description';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import SendIcon from '@material-ui/icons/Send';
import GroupIcon from '@material-ui/icons/Group';

// The full-page notification sheet, shared by the bell panel in sidebar.js and
// the Sent tab of pages/sendNotification. One component rather than two copies:
// they are meant to be the same thing, and a duplicate would drift the first
// time either is touched.
//
// `color` paints the dot and the unread bar in the bell list; `deep` is a
// darkened `color` for a label sitting on `soft` (plain `color` only manages
// 3.89:1 for warning, which fails at 12px).
//
// `grad` is the hero. The stops are picked so plain white clears AA on the
// LIGHT end, not just the dark one - 6.11 / 5.14 / 5.28 / 5.99. A brand-style
// gradient running out to #ff9c66 would put white at 3.18 by its last stop.
// `ink` is the dark stop reused as text on a white chip (10:1 and up).
// Keyed by a category's TONE, not by severity.
//
// Severity said how alarmed to be; a category says what changed. The database
// stores only the tone KEY - the hex values live here, because every pairing
// below has been checked together: white on the gradient's lightest stop, on
// `ink`, and `deep` on `soft` for the chip. Lowest in the set is 5.14:1.
// Letting the DB store raw hex would mean one careless insert could put
// unreadable text on the bell, the hero and the Sent table at once.
export const NOTIF_TONES = {
  blue: {
    color: '#2c5fb8', soft: '#eef3fc', deep: '#2c5fb8', ink: '#16336b',
    grad: 'linear-gradient(135deg, #16336b 0%, #23498f 45%, #2c5fb8 100%)',
    Icon: InfoOutlinedIcon,
  },
  green: {
    color: '#1e7e34', soft: '#eaf6ee', deep: '#19692c', ink: '#11492a',
    grad: 'linear-gradient(135deg, #11492a 0%, #17622f 45%, #1e7e34 100%)',
    Icon: CheckCircleOutlineIcon,
  },
  amber: {
    color: '#b26a00', soft: '#fff4e2', deep: '#7a4b00', ink: '#5e3900',
    grad: 'linear-gradient(135deg, #5e3900 0%, #7d4b00 45%, #9c5d00 100%)',
    Icon: WarningRoundedIcon,
  },
  red: {
    color: '#c0392b', soft: '#fdecea', deep: '#a93226', ink: '#7d1f14',
    grad: 'linear-gradient(135deg, #7d1f14 0%, #99291f 45%, #b5352a 100%)',
    Icon: ErrorOutlineIcon,
  },
  violet: {
    color: '#6f40a0', soft: '#f3edf9', deep: '#5a3380', ink: '#44265e',
    grad: 'linear-gradient(135deg, #44265e 0%, #5a3380 45%, #6f40a0 100%)',
    Icon: NewReleasesIcon,
  },
  teal: {
    color: '#13746f', soft: '#e6f4f3', deep: '#0f5c59', ink: '#0b4442',
    grad: 'linear-gradient(135deg, #0b4442 0%, #0f5c59 45%, #13746f 100%)',
    Icon: GavelIcon,
  },
  slate: {
    color: '#4f596a', soft: '#eef1f5', deep: '#4f596a', ink: '#242c38',
    grad: 'linear-gradient(135deg, #242c38 0%, #333e4d 45%, #4f596a 100%)',
    Icon: CampaignIcon,
  },
  orange: {
    color: '#a8450f', soft: '#fff1ea', deep: '#a8450f', ink: '#7a3d12',
    grad: 'linear-gradient(135deg, #7a3d12 0%, #93491a 45%, #a8450f 100%)',
    Icon: SwapHorizIcon,
  },
};

// A notification carries its tone and name as a SNAPSHOT taken at send time,
// so a category renamed next year does not rewrite what past announcements
// claimed to be. Falls back to slate rather than throwing on an old row.
export const toneOf = (notification) => NOTIF_TONES[notification?.categoryTone] || NOTIF_TONES.slate;
export const categoryLabelOf = (notification) => notification?.categoryName || 'Notification';

// 0 is a real size, so the guard has to come before the falsy check - multer
// reports 0 bytes for an empty upload and "" is more honest than "NaN".
export const formatBytes = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// Picks the tile icon from the mime type first and the extension second. S3
// stores both, but multerS3's AUTO_CONTENT_TYPE falls back to
// application/octet-stream for anything it cannot sniff, in which case the
// filename is all there is to go on.
const attachmentVisual = (att = {}) => {
  const mime = String(att.mimeType || '').toLowerCase();
  const ext = String(att.name || '').split('.').pop().toLowerCase();

  if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
    return { Icon: ImageIcon, color: '#7a4b9d', soft: '#f3edf9' };
  }
  if (mime === 'application/pdf' || ext === 'pdf') {
    return { Icon: PictureAsPdfIcon, color: '#b3261e', soft: '#fdecea' };
  }
  if (mime.includes('sheet') || mime.includes('excel') || ['xls', 'xlsx', 'csv'].includes(ext)) {
    return { Icon: TableChartIcon, color: '#1e7e34', soft: '#eaf6ee' };
  }
  if (mime.includes('word') || mime.includes('document') || ['doc', 'docx', 'txt', 'rtf'].includes(ext)) {
    return { Icon: DescriptionIcon, color: '#2c5fb8', soft: '#eef3fc' };
  }
  return { Icon: InsertDriveFileIcon, color: '#6b6460', soft: '#f3f1ef' };
};

// Turns the stored `link` into an absolute URL for a new tab.
//
// The compose field asks for an in-app path ("/cron-job-monitor") and the app
// runs on BrowserRouter, so origin + path is the whole job - but it is free
// text, so a bare path and a full URL both have to survive it too. new URL()
// with the origin as base handles all three.
//
// Anything that is not http(s) is dropped rather than rendered: a
// "javascript:" href executes on click, and an href is not the place to lean
// on the fact that only the tech team can author a notification.
const resolveHref = (link) => {
  if (!link) return '';
  try {
    const url = new URL(String(link).trim(), window.location.origin);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
    return url.href;
  } catch (err) {
    return '';
  }
};

// Slide up, so the sheet rises from the bottom edge rather than fading in the
// middle like an alert box.
const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles({
  // ---- notification detail: full-page sheet -------------------------------
  // A fullScreen Dialog sliding up from the bottom, not a centred box. It is
  // still a Dialog, so it stays on this route - no navigation, nothing to come
  // back from, and the bell panel is still open underneath when it closes.
  //
  // MUI's paperFullScreen already sets width/height 100% and kills the radius;
  // what it does NOT do is stop the paper itself scrolling, which would scroll
  // the hero off the top along with the body. Pinning overflow here makes the
  // middle section the only scroller.
  notifDlgPaper: {
    overflow: 'hidden',
    // Spelled out as well as the shorthand: .MuiDialog-paper sets overflowY:
    // auto, and only the long-hand reliably beats a long-hand at equal
    // specificity whatever order the sheets land in. Without this the paper
    // itself can scroll, taking the hero and the footer with it.
    overflowY: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    background: '#fbfaf9',
  },
  // Full-bleed hero. The gradient is the category's tone, so an Urgent notice
  // is recognisable from across the room before a word is read.
  notifDlgHero: {
    position: 'relative',
    flexShrink: 0,
    overflow: 'hidden',
    padding: '26px 32px 24px',
    color: '#fff',
    '@media (max-width:599.95px)': { padding: '20px 18px 18px' },
  },
  notifDlgHeroGlyph: {
    position: 'absolute', top: -56, right: -30,
    fontSize: 240, opacity: 0.1, pointerEvents: 'none', transform: 'rotate(-12deg)',
    '@media (max-width:599.95px)': { fontSize: 150, top: -30, right: -20 },
  },
  // Centred on the same measure as the body, so the title sits directly above
  // the paragraph it introduces instead of drifting to the far left on a wide
  // monitor.
  notifDlgHeroInner: {
    position: 'relative',
    maxWidth: 880, margin: '0 auto',
    display: 'flex', alignItems: 'flex-start', gap: 16,
  },
  notifDlgHeroIcon: {
    width: 52, height: 52, borderRadius: 16, flexShrink: 0,
    display: 'grid', placeItems: 'center',
    background: 'rgba(255,255,255,0.18)',
    border: '1px solid rgba(255,255,255,0.28)',
    '@media (max-width:599.95px)': { width: 42, height: 42, borderRadius: 13 },
  },
  notifDlgEyebrow: {
    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '5px 10px',
    fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.92)',
  },
  notifDlgEyebrowDot: { opacity: 0.55 },
  notifDlgTitle: {
    fontSize: 26, fontWeight: 700, lineHeight: 1.25, marginTop: 9,
    letterSpacing: -0.3, wordBreak: 'break-word',
    '@media (max-width:599.95px)': { fontSize: 20 },
  },
  // Darkened, not frosted. A white-tinted panel LIGHTENS the hero and drops
  // white text to 3.35-4.68 depending on the tone - below AA on every one of
  // them. Darkening takes it to 7.16-8.31 and reads as an inset well rather
  // than a pane floating on a pane.
  notifDlgSenderCard: {
    display: 'inline-flex', alignItems: 'center', gap: 11, marginTop: 16,
    padding: '8px 16px 8px 8px', borderRadius: 14,
    background: 'rgba(0,0,0,0.20)',
    border: '1px solid rgba(255,255,255,0.16)',
  },
  notifDlgSenderAvatar: {
    width: 34, height: 34, borderRadius: '50%', display: 'grid', placeItems: 'center',
    fontSize: 14, fontWeight: 700, background: '#fff', flexShrink: 0,
  },
  notifDlgSenderName: { fontSize: 13.5, fontWeight: 700, lineHeight: 1.25 },
  notifDlgSenderRole: { fontSize: 11, color: 'rgba(255,255,255,0.88)', marginTop: 1 },
  notifDlgSenderTime: {
    marginLeft: 18, paddingLeft: 18, flexShrink: 0,
    borderLeft: '1px solid rgba(255,255,255,0.22)',
    fontSize: 11.5, color: 'rgba(255,255,255,0.94)',
  },
  notifDlgNewChip: {
    fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase',
    background: '#fff', borderRadius: 999, padding: '3px 10px', flexShrink: 0,
  },
  // 44px box, because on a full page this is the ONLY way out besides Esc.
  notifDlgHeroClose: {
    position: 'absolute', top: 16, right: 20, zIndex: 2,
    width: 44, height: 44, color: '#fff',
    background: 'rgba(0,0,0,0.22)',
    border: '1px solid rgba(255,255,255,0.22)',
    transition: 'background-color 200ms ease, transform 200ms ease',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.38)', transform: 'rotate(90deg)' },
    '@media (max-width:599.95px)': { top: 10, right: 10, width: 38, height: 38 },
  },
  // The only scrolling element. minHeight:0 is load-bearing - without it this
  // flex child refuses to shrink below its content and pushes the footer off
  // the bottom of the viewport.
  notifDlgScroll: {
    flex: 1, minHeight: 0, overflowY: 'auto',
    padding: '30px 32px 44px',
    '@media (max-width:599.95px)': { padding: '20px 18px 32px' },
  },
  // Capped measure. Full-bleed body text on a 27" monitor runs to ~200
  // characters a line, which is unreadable however much space there is.
  notifDlgSheet: {
    maxWidth: 880, margin: '0 auto',
    background: '#fff', borderRadius: 18, border: '1px solid #eeeae7',
    boxShadow: '0 2px 14px rgba(93,55,28,0.06)',
    padding: '28px 30px',
    '@media (max-width:599.95px)': { padding: '20px 18px', borderRadius: 14 },
  },
  notifDlgFooter: {
    flexShrink: 0,
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '14px 32px', background: '#fff', borderTop: '1px solid #ece8e5',
    '@media (max-width:599.95px)': { padding: '12px 16px' },
  },
  notifDlgFooterInner: {
    width: '100%', maxWidth: 880, margin: '0 auto',
    display: 'flex', alignItems: 'center', gap: 12,
  },
  notifDlgEscHint: {
    fontSize: 11.5, color: '#756e68',
    '@media (max-width:599.95px)': { display: 'none' },
  },
  notifDlgKbd: {
    display: 'inline-block', padding: '1px 6px', borderRadius: 5,
    border: '1px solid #ddd6d1', background: '#f7f4f2',
    fontSize: 10.5, fontWeight: 700, color: '#5f574f',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  },
  // Tinted with the category rather than grey-on-grey, so it belongs to the
  // same page as the hero above it. The colours arrive inline (they depend on
  // the row) - which also pins them through :hover, since an inline style
  // outranks MUI's own hover background with no !important needed.
  notifDlgBtnGhost: {
    marginLeft: 'auto',
    borderRadius: 24, textTransform: 'none', fontWeight: 700, padding: '7px 22px',
    border: '1px solid transparent', boxShadow: 'none',
    transition: 'box-shadow 180ms ease, transform 180ms ease',
    // Lifts instead of recolouring on hover - a fill change here would read as
    // the button being pressed.
    '&:hover': { boxShadow: '0 4px 12px rgba(93,55,28,0.16)', transform: 'translateY(-1px)' },
  },
  notifDlgBtnPrimary: {
    borderRadius: 24, textTransform: 'none', fontWeight: 700, padding: '7px 24px',
    color: '#fff', boxShadow: '0 6px 16px rgba(93,55,28,0.22)',
    '&:hover': { boxShadow: '0 6px 16px rgba(93,55,28,0.22)' },
  },
  // Quill's own output. Margins are restored here (the bell list strips them
  // for its three-line clamp) because this is where the formatting is meant
  // to be read.
  notifDlgMessage: {
    fontSize: 15.5, lineHeight: 1.75, color: '#4a4440', wordBreak: 'break-word',
    '& p': { margin: '0 0 13px' },
    '& p:last-child': { marginBottom: 0 },
    '& ul, & ol': { margin: '0 0 10px', paddingLeft: 22 },
    '& li': { marginBottom: 4 },
    '& h1, & h2, & h3': { margin: '14px 0 6px', color: '#2f2a27', lineHeight: 1.3 },
    '& h1': { fontSize: 21 }, '& h2': { fontSize: 18 }, '& h3': { fontSize: 16 },
    '& a': { color: '#c1531f', fontWeight: 600 },
    '& strong, & b': { color: '#2f2a27' },
    '& img': { maxWidth: '100%', height: 'auto', borderRadius: 8 },
    '& blockquote': {
      margin: '0 0 10px', padding: '2px 0 2px 12px',
      borderLeft: '3px solid #f3c9b2', color: '#6b6460',
    },
  },
  notifDlgSectionTitle: {
    display: 'flex', alignItems: 'center', gap: 8, margin: '26px 0 13px',
    fontSize: 11.5, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: '#756e68',
  },
  notifDlgSectionRule: { flex: 1, height: 1, background: '#f1eeec' },
  notifDlgFiles: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(248px, 1fr))', gap: 12 },
  // An <a>, not a div with an onClick: middle-click, "open in new tab" and
  // "save link as" all come free, and the url is visible in the status bar
  // before the click.
  notifDlgFile: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
    border: '1px solid #ece8e5', borderRadius: 12, background: '#fbfaf9',
    textDecoration: 'none', color: 'inherit', minWidth: 0,
    transition: 'border-color 160ms ease, background-color 160ms ease',
    '&:hover': { borderColor: '#f3c9b2', background: '#fff8f4' },
  },
  notifDlgFileIcon: {
    width: 34, height: 34, borderRadius: 9, flexShrink: 0, display: 'grid', placeItems: 'center',
  },
  notifDlgFileName: {
    fontSize: 12.5, fontWeight: 600, color: '#2f2a27',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  notifDlgFileSize: { fontSize: 11, color: '#756e68', marginTop: 1 },
  notifDlgFileGet: { fontSize: 18, color: '#c1531f', flexShrink: 0 },

  notifDlgSectionCount: {
    fontSize: 10, fontWeight: 800, color: '#a8450f',
    background: '#fff1ea', borderRadius: 999, padding: '1px 7px',
  },
  // ---- delivery stats (Sent tab only) ------------------------------------
  // Rendered only when the row carries recipientCount, which /sent returns and
  // /mine does not - so the bell shows nothing and no caller has to ask.
  notifDlgStats: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12,
  },
  notifDlgStat: {
    padding: '13px 16px', borderRadius: 14,
    background: '#fbfaf9', border: '1px solid #ece8e5',
  },
  notifDlgStatValue: { fontSize: 22, fontWeight: 700, color: '#3d2b21', lineHeight: 1.2 },
  notifDlgStatLabel: {
    fontSize: 10.5, fontWeight: 800, letterSpacing: 0.7, textTransform: 'uppercase',
    color: '#756e68', marginTop: 4,
  },
  notifDlgStatHint: { fontSize: 11.5, color: '#756e68', marginTop: 3 },
  notifDlgAudience: {
    display: 'flex', alignItems: 'flex-start', gap: 9, marginTop: 12,
    padding: '11px 14px', borderRadius: 12,
    background: '#fff1ea', border: '1px solid #f3c9b2',
    fontSize: 12.5, color: '#5f574f', lineHeight: 1.5,
  },
  notifDlgAudienceLabel: { fontWeight: 800, color: '#a8450f' },
});

// `notification` is the row itself. The bell (/mine) and the Sent tab (/sent)
// name the same two fields differently, so both spellings are accepted here
// rather than making each caller reshape its data:
//   sentBy | createdByName      sentAt | createdAt
const NotificationDetailSheet = ({ notification, onClose }) => {
  const classes = useStyles();

  return (
    <Dialog
      open={Boolean(notification)}
      onClose={onClose}
      TransitionComponent={SlideUp}
      fullScreen
      classes={{ paper: classes.notifDlgPaper }}
      aria-labelledby="notification-detail-title"
    >
      {/* Guarded rather than relying on `open`: MUI keeps the Paper mounted
          through the closing transition, and the caller's state is already
          null by then. */}
      {notification && (() => {
        const tone = toneOf(notification);
        const ToneIcon = tone.Icon;
        const categoryLabel = categoryLabelOf(notification);
        const files = notification.attachments || [];
        const sender = notification.sentBy || notification.createdByName || '';
        const sentAt = moment(notification.sentAt || notification.createdAt);
        // Only the bell carries readAt; on the Sent tab the sender is looking
        // at their own message, where "New" would be meaningless.
        const isUnread = 'readAt' in notification && !notification.readAt;
        const href = resolveHref(notification.link);

        return (
          <>
            <div className={classes.notifDlgHero} style={{ background: tone.grad }}>
              {/* Decorative - the icon chip and the word beside it carry the
                  same meaning for a screen reader. */}
              <ToneIcon className={classes.notifDlgHeroGlyph} aria-hidden="true" />

              <IconButton
                className={classes.notifDlgHeroClose}
                onClick={onClose}
                aria-label="close notification"
              >
                <ClearIcon />
              </IconButton>

              <div className={classes.notifDlgHeroInner}>
                <div className={classes.notifDlgHeroIcon}>
                  <ToneIcon style={{ fontSize: 28 }} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className={classes.notifDlgEyebrow}>
                    <span>{categoryLabel}</span>
                    <span className={classes.notifDlgEyebrowDot}>&bull;</span>
                    {/* Absolute here, relative in the sender card - they
                        answer different questions: the stamp for "was this
                        before or after the 4pm change?", "2 hours ago" for
                        recency. */}
                    <span>{sentAt.format('DD MMM YYYY, h:mm A')}</span>
                    {isUnread && (
                      <span className={classes.notifDlgNewChip} style={{ color: tone.ink }}>New</span>
                    )}
                  </div>

                  <div className={classes.notifDlgTitle} id="notification-detail-title">
                    {notification.title}
                  </div>

                  <div className={classes.notifDlgSenderCard}>
                    <span className={classes.notifDlgSenderAvatar} style={{ color: tone.ink }}>
                      {String(sender || '?').trim().charAt(0).toUpperCase()}
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <div className={classes.notifDlgSenderName}>{sender || 'Decorpot ERP'}</div>
                      <div className={classes.notifDlgSenderRole}>Sender</div>
                    </span>
                    <span className={classes.notifDlgSenderTime}>
                      <AccessTimeIcon style={{ fontSize: 12, verticalAlign: 'middle', marginRight: 5 }} />
                      {sentAt.fromNow()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className={classes.notifDlgScroll}>
              <div className={classes.notifDlgSheet}>
                {notification.messageHtml
                  ? (
                    <div
                      className={classes.notifDlgMessage}
                      dangerouslySetInnerHTML={{ __html: notification.messageHtml }}
                    />
                  ) : (
                    // Plain text keeps its line breaks - the editor was added
                    // later, so older rows have message only.
                    <div className={classes.notifDlgMessage} style={{ whiteSpace: 'pre-wrap' }}>
                      {notification.message}
                    </div>
                  )}

                {files.length > 0 && (
                  <>
                    <div className={classes.notifDlgSectionTitle}>
                      <AttachFileIcon style={{ fontSize: 15 }} />
                      Attachment{files.length === 1 ? '' : 's'}
                      <span className={classes.notifDlgSectionCount}>{files.length}</span>
                      <span className={classes.notifDlgSectionRule} />
                    </div>
                    <div className={classes.notifDlgFiles}>
                      {files.map((file, i) => {
                        const visual = attachmentVisual(file);
                        const FileIcon = visual.Icon;
                        const size = formatBytes(file.size);
                        return (
                          <a
                            key={file.url || i}
                            className={classes.notifDlgFile}
                            href={file.url}
                            target="_blank"
                            // Without noreferrer the opened tab gets a
                            // window.opener handle back into the ERP.
                            rel="noopener noreferrer"
                            title={file.name}
                          >
                            <span
                              className={classes.notifDlgFileIcon}
                              style={{ background: visual.soft, color: visual.color }}
                            >
                              <FileIcon style={{ fontSize: 19 }} />
                            </span>
                            <span style={{ minWidth: 0, flex: 1 }}>
                              <div className={classes.notifDlgFileName}>{file.name || 'Attachment'}</div>
                              <div className={classes.notifDlgFileSize}>
                                {size ? `${size} · ` : ''}Opens in a new tab
                              </div>
                            </span>
                            <GetAppIcon className={classes.notifDlgFileGet} />
                          </a>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Delivery stats. Keyed off recipientCount, which /sent
                    returns and /mine does not - so the bell shows nothing and
                    neither caller has to pass a flag. */}
                {typeof notification.recipientCount === 'number' && (() => {
                  const total = notification.recipientCount || 0;
                  const opened = notification.readCount || 0;
                  const pct = total ? Math.round((opened / total) * 100) : 0;
                  const mail = notification.emailStatus || {};
                  let mailValue = 'Off';
                  let mailHint = 'Not sent by email';
                  if (mail.requested) {
                    mailValue = String(mail.sent || 0);
                    mailHint = mail.failed
                      ? `${mail.failed} failed`
                      : (mail.attempted ? 'all delivered' : 'queued');
                  }
                  return (
                    <>
                      <div className={classes.notifDlgSectionTitle}>
                        <SendIcon style={{ fontSize: 15 }} />
                        Delivery
                        <span className={classes.notifDlgSectionRule} />
                      </div>
                      <div className={classes.notifDlgStats}>
                        <div className={classes.notifDlgStat}>
                          <div className={classes.notifDlgStatValue}>{total}</div>
                          <div className={classes.notifDlgStatLabel}>Recipients</div>
                          <div className={classes.notifDlgStatHint}>in-app notification</div>
                        </div>
                        <div className={classes.notifDlgStat}>
                          <div className={classes.notifDlgStatValue}>{opened}</div>
                          <div className={classes.notifDlgStatLabel}>Opened</div>
                          <div className={classes.notifDlgStatHint}>{pct}% of recipients</div>
                        </div>
                        <div className={classes.notifDlgStat}>
                          <div className={classes.notifDlgStatValue}>{mailValue}</div>
                          <div className={classes.notifDlgStatLabel}>Emailed</div>
                          <div className={classes.notifDlgStatHint}>{mailHint}</div>
                        </div>
                      </div>
                      {notification.audienceSummary && (
                        <div className={classes.notifDlgAudience}>
                          <GroupIcon style={{ fontSize: 16, color: '#a8450f', flexShrink: 0, marginTop: 1 }} />
                          <span>
                            <span className={classes.notifDlgAudienceLabel}>Sent to&nbsp;</span>
                            {notification.audienceSummary}
                          </span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            <div className={classes.notifDlgFooter}>
              <div className={classes.notifDlgFooterInner}>
                <span className={classes.notifDlgEscHint}>
                  Press <span className={classes.notifDlgKbd}>Esc</span> to go back
                </span>
                <Button
                  className={classes.notifDlgBtnGhost}
                  style={{ background: tone.soft, color: tone.deep, borderColor: `${tone.color}44` }}
                  onClick={onClose}
                  startIcon={<ClearIcon style={{ fontSize: 16 }} />}
                >
                  Close
                </Button>
                {href && (
                  // A real anchor, not a Button with an onClick: target opens
                  // the new tab natively, so middle-click, ctrl/cmd-click and
                  // "open in new tab" all behave, and the destination shows in
                  // the status bar before the click. Without noreferrer the
                  // opened tab gets a window.opener handle back into the ERP.
                  //
                  // Carries the category down to the footer, so the page is one
                  // object rather than a coloured lid on a generic sheet.
                  <Button
                    className={classes.notifDlgBtnPrimary}
                    style={{ background: tone.grad }}
                    component="a"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    // Deliberately does NOT close the sheet. The page opens in
                    // a separate tab, so this one has not gone anywhere -
                    // closing it would throw away what the reader was looking
                    // at without being asked. Closing is the reader's call.
                    startIcon={<OpenInNewIcon style={{ fontSize: 16 }} />}
                  >
                    Open page
                  </Button>
                )}
              </div>
            </div>
          </>
        );
      })()}
    </Dialog>
  );
};

export default NotificationDetailSheet;
